import {
  build_case_fact_items,
  case_status_tone,
  fetch_requestor_application_documents,
  fetch_requestor_application_forms,
  first_record_path,
  first_truthy,
  format_date,
  map_documents,
  normalize_text,
  record_domain_label,
  record_status_id,
  record_status_label,
  to_number,
} from './request_portal_helpers';
import { fetch_assign_form_status_data } from '@/services/workflow_service';

const APPLICATION_ID_PATHS = [
  ['application_id'],
  ['latest_application_id'],
  ['current_application_id'],
];
const ENTITY_PROFILE_ID_PATHS = [['entity_profile_id'], ['id']];
const ENTITY_TYPE_ID_PATHS = [
  ['entity_type_id'],
  ['entity_type', 'id'],
  ['entity_type'],
];

function preferred_application_form(application_forms = [], case_type = {}) {
  const preferred_form_ids = new Set((case_type?.form_ids || []).map(Number));
  return application_forms.find((application_form) =>
    preferred_form_ids.has(Number(application_form?.form?.id || 0))
  ) || application_forms[0] || null;
}

async function application_case_details(context, application_id, case_type, record = {}) {
  if (!application_id) {
    return {
      answers: [],
      answer_sections: [],
      application_form_id: null,
      documents: [],
      workflow_state: fallback_workflow_state(record),
    };
  }

  const [form_details, documents] = await Promise.all([
    load_application_answers(context, application_id, case_type, record),
    load_application_documents(application_id),
  ]);

  return { ...form_details, documents };
}

export async function hydrate_case_record_details(context, case_item = {}) {
  if (case_item?.details_loaded) {
    return case_item;
  }

  const case_type = case_item?.source_case_type || {};
  const record = case_item?.source_record || {};
  const details = await application_case_details(context, case_item?.application_id, case_type, record);
  const facts = build_case_fact_items(details.answers);
  const { entity_profile_name, title } = title_fields(record, case_type, facts, {
    application_id: case_item?.application_id,
    entity_profile_id: case_item?.entity_profile_id,
  });

  return {
    ...case_item,
    application_form_id: details.application_form_id,
    workflow_state: details.workflow_state,
    is_initial_workflow_status: Boolean(details.workflow_state?.is_initial),
    is_final_workflow_status: Boolean(details.workflow_state?.is_final),
    case_facts: facts,
    case_fact_sections: details.answer_sections,
    documents: details.documents,
    entity_profile_name: entity_profile_name || title,
    title,
    details_loaded: true,
    details_loading: false,
  };
}

export async function hydrate_case_record_display(context, case_item = {}) {
  if (!case_item?.application_id) {
    return case_item;
  }

  const case_type = case_item?.source_case_type || {};
  const record = case_item?.source_record || {};
  const details = await load_application_answers(context, case_item.application_id, case_type, record);
  const facts = build_case_fact_items(details.answers);
  const { entity_profile_name, title } = title_fields(record, case_type, facts, {
    application_id: case_item?.application_id,
    entity_profile_id: case_item?.entity_profile_id,
  });

  return {
    ...case_item,
    application_form_id: details.application_form_id || case_item.application_form_id,
    workflow_state: details.workflow_state || case_item.workflow_state,
    is_initial_workflow_status: Boolean(details.workflow_state?.is_initial),
    is_final_workflow_status: Boolean(details.workflow_state?.is_final),
    case_facts: facts.length ? facts : case_item.case_facts,
    case_fact_sections: details.answer_sections.length ? details.answer_sections : case_item.case_fact_sections,
    entity_profile_name: entity_profile_name || title,
    title,
  };
}

async function load_application_answers(context, application_id, case_type, record = {}) {
  try {
    const application_forms = await fetch_requestor_application_forms(application_id);
    const form = preferred_application_form(application_forms, case_type);
    const workflow_state = await workflow_state_for_case(context, form, record);

    return {
      application_form_id: to_number(form?.application_form_id),
      answers: context.flatten_application_form_answers(form),
      answer_sections: context.flatten_application_form_answer_sections(form),
      workflow_state,
    };
  } catch {
    return {
      answers: [],
      answer_sections: [],
      application_form_id: null,
      workflow_state: fallback_workflow_state(record),
    };
  }
}

function assigned_status_records(response = {}) {
  const payload = [
    response?.assigned_statuses,
    response?.data?.assigned_statuses,
    response?.selected_statuses,
    response?.data?.selected_statuses,
    [],
  ].find((value) => value !== undefined && value !== null);

  return Array.isArray(payload) ? payload : [];
}

function normalized_workflow_status(status = {}, index = 0) {
  return {
    id: to_number(first_truthy([status?.id, status?.assigned_form_status_id])),
    status_id: to_number(first_truthy([status?.status_id, status?.workflow_status_id])),
    name: normalize_text(first_truthy([
      status?.status_name,
      status?.workflow_status_name,
      status?.status,
      status?.name,
      `Status ${index + 1}`,
    ])),
    is_initial: Boolean(status?.is_initial),
    is_final: Boolean(status?.is_final),
  };
}

function workflow_status_cache(context) {
  if (!(context._requestor_workflow_status_cache instanceof Map)) {
    context._requestor_workflow_status_cache = new Map();
  }

  return context._requestor_workflow_status_cache;
}

async function assigned_workflow_statuses(context, form_id = null) {
  const normalized_form_id = to_number(form_id);
  if (!normalized_form_id) {
    return [];
  }

  const cache = workflow_status_cache(context);
  if (cache.has(normalized_form_id)) {
    return cache.get(normalized_form_id);
  }

  try {
    const response = await fetch_assign_form_status_data({ form_id: normalized_form_id });
    const statuses = assigned_status_records(response)
      .map(normalized_workflow_status)
      .filter((status) => status.id && status.name);
    cache.set(normalized_form_id, statuses);
    return statuses;
  } catch {
    cache.set(normalized_form_id, []);
    return [];
  }
}

function workflow_status_matcher(record = {}, form = {}) {
  return {
    status_id: to_number(first_truthy([
      record_status_id(record),
      form?.assigned_form_status_id,
      form?.current_status_id,
      form?.workflow_status_id,
      form?.status_id,
      form?.current_status?.id,
      form?.workflow_status?.id,
      form?.status?.id,
    ])),
    status_name: normalize_text(first_truthy([
      record_status_label(record),
      form?.current_status_name,
      form?.workflow_status_name,
      form?.status_name,
      form?.current_status?.status_name,
      form?.workflow_status?.status_name,
      form?.status?.status_name,
      form?.current_status?.name,
      form?.workflow_status?.name,
      form?.status?.name,
    ])).toLowerCase(),
  };
}

function matching_workflow_status(statuses = [], matcher = {}) {
  const status_id = to_number(matcher.status_id);
  const status_name = normalize_text(matcher.status_name).toLowerCase();

  return statuses.find((status) =>
    (status_id && [status.id, status.status_id].some((candidate) => Number(candidate) === Number(status_id)))
    || (status_name && normalize_text(status.name).toLowerCase() === status_name)
  ) || null;
}

function fallback_workflow_state(record = {}) {
  const status_name = record_status_label(record);
  return {
    name: status_name,
    is_initial: false,
    is_final: normalize_text(status_name).toLowerCase() === 'closed',
  };
}

async function workflow_state_for_case(context, form = {}, record = {}) {
  const form_id = form?.form?.id || form?.form_id || form?.id;
  const statuses = await assigned_workflow_statuses(context, form_id);
  const matched_status = matching_workflow_status(statuses, workflow_status_matcher(record, form));
  return matched_status || fallback_workflow_state(record);
}

async function load_application_documents(application_id) {
  try {
    return map_documents(
      await fetch_requestor_application_documents(application_id).then((documents) =>
        (Array.isArray(documents) ? documents : []).map((document) => ({
          ...document,
          application_id,
        }))
      )
    );
  } catch {
    return [];
  }
}

function case_events(created, status) {
  return [
    { title: 'Case submitted', meta: format_date(created), state: 'complete' },
    status ? { title: status, meta: 'Latest status', state: 'current' } : null,
  ].filter(Boolean);
}

function entity_profile_name(record = {}) {
  return normalize_text(first_truthy([
    record?.entity_name,
    record?.name,
    record?.profile_name,
  ]));
}

function fallback_case_title(case_type = {}, application_id, entity_profile_id) {
  return `${first_truthy([case_type?.title, 'Case'])} ${first_truthy([application_id, entity_profile_id, ''])}`;
}

function first_fact_value(facts = []) {
  return first_truthy(facts.map((fact) => fact.value));
}

function case_title(record = {}, case_type = {}, facts = [], application_id, entity_profile_id) {
  return case_title_result(record, case_type, facts, application_id, entity_profile_id);
}

function case_title_result(record = {}, case_type = {}, facts = [], application_id, entity_profile_id) {
  const profile_name = entity_profile_name(record);
  const title = normalize_text(first_truthy([
    first_fact_value(facts),
    profile_name,
    fallback_case_title(case_type, application_id, entity_profile_id),
  ]));

  return { entity_profile_name: profile_name, title };
}

function application_id(record = {}) {
  return to_number(first_record_path(record, APPLICATION_ID_PATHS));
}

function entity_profile_id(record = {}) {
  return to_number(first_record_path(record, ENTITY_PROFILE_ID_PATHS));
}

function entity_type_id(record = {}, case_type = {}) {
  return to_number(first_truthy([first_record_path(record, ENTITY_TYPE_ID_PATHS), case_type?.id]));
}

function case_identity(record = {}, case_type = {}) {
  return {
    application_id: application_id(record),
    entity_profile_id: entity_profile_id(record),
    entity_type_id: entity_type_id(record, case_type),
  };
}

function created_date(record = {}) {
  return record?.date_created || record?.created_at || record?.application_date_created;
}

function backend_reference(record = {}) {
  return normalize_text(first_truthy([
    record?.reference_number,
    record?.case_code,
    record?.reference,
    record?.code,
  ]));
}

function case_reference(record = {}, application_id_value, entity_profile_id_value) {
  const resolved_reference = backend_reference(record);
  if (resolved_reference) {
    return resolved_reference;
  }

  const references = new Map([
    [true, `C-${application_id_value}`],
    [false, `EP-${first_truthy([entity_profile_id_value, '-'])}`],
  ]);
  return references.get(Boolean(application_id_value));
}

function case_code(record = {}, application_id_value, entity_profile_id_value) {
  const resolved_reference = backend_reference(record);
  if (resolved_reference) {
    return resolved_reference;
  }

  const codes = new Map([
    [true, `C-${application_id_value}`],
    [false, `EP-${first_truthy([entity_profile_id_value, 'new'])}`],
  ]);
  return codes.get(Boolean(application_id_value));
}

function payload_facts(record = {}, case_type = {}, application_id_value, entity_profile_id_value, created) {
  return [
    { label: 'Case type', value: case_type?.title || 'Case' },
    { label: 'Reference', value: case_reference(record, application_id_value, entity_profile_id_value) },
    { label: 'Opened', value: format_date(created) },
  ];
}

function payload_identity_fields(record = {}, identity = {}, case_type = {}, title = '') {
  const { application_id: application_id_value, entity_profile_id: entity_profile_id_value } = identity;
  return {
    id: first_truthy([application_id_value, entity_profile_id_value, `${case_type.id}-${title}`]),
    application_id: application_id_value,
    entity_profile_id: entity_profile_id_value,
    entity_type_id: identity.entity_type_id,
    code: case_code(record, application_id_value, entity_profile_id_value),
  };
}

function payload_status_fields(status = '') {
  return {
    status,
    status_class: '',
    tone: case_status_tone(status),
    update_title: 'Case opened',
    update_body: first_truthy([status && `Current status: ${status}`, '']),
  };
}

function payload_progress_fields() {
  return {
    progress_class: '',
    progress_steps: ['complete', 'current', 'pending', 'pending', 'pending'],
  };
}

function payload_actions() {
  return ['Message handler', `Upload documents`];
}

function payload_dates(created) {
  return {
    opened: format_date(created),
    sort_time: Number(first_truthy([created && new Date(created).getTime(), 0])),
    date: format_date(created),
  };
}

function title_fields(record = {}, case_type = {}, facts = [], identity = {}) {
  return case_title(
    record,
    case_type,
    facts,
    identity.application_id,
    identity.entity_profile_id
  );
}

function base_case_payload({
  record,
  case_type,
  identity,
  created,
  domain,
  status,
  workflow_state,
  facts,
  documents,
  application_form_id,
  answer_sections,
}) {
  return assembled_case_payload({
    record,
    case_type,
    identity,
    created,
    domain,
    status,
    workflow_state,
    facts,
    documents,
    application_form_id,
    answer_sections,
  });
}

function assembled_case_payload({
  record,
  case_type,
  identity,
  created,
  domain,
  status,
  workflow_state,
  facts,
  documents,
  application_form_id,
  answer_sections,
}) {
  const { entity_profile_name, title } = title_fields(record, case_type, facts, identity);
  const identity_fields = payload_identity_fields(record, identity, case_type, title);
  return Object.assign(
    {},
    identity_fields,
    payload_dates(created),
    payload_status_fields(status),
    payload_progress_fields(),
    payload_case_details({
      record,
      application_form_id,
      entity_profile_name,
      title,
      domain,
      case_type,
      identity,
      created,
      status,
      workflow_state,
      facts,
      answer_sections,
      documents,
    })
  );
}

function payload_case_details({
  record,
  application_form_id,
  entity_profile_name,
  title,
  domain,
  case_type,
  identity,
  created,
  status,
  workflow_state,
  facts,
  answer_sections,
  documents,
}) {
  return {
    application_form_id,
    workflow_state,
    is_initial_workflow_status: Boolean(workflow_state?.is_initial),
    is_final_workflow_status: Boolean(workflow_state?.is_final),
    details_loaded: false,
    details_loading: false,
    source_record: record,
    source_case_type: case_type,
    entity_profile_name: entity_profile_name || title,
    type_label: domain,
    type_class: '',
    title,
    facts: payload_facts(record, case_type, identity.application_id, identity.entity_profile_id, created),
    events: case_events(created, status),
    case_facts: facts,
    case_fact_sections: answer_sections,
    documents,
    actions: payload_actions(documents),
  };
}

export default {
    async map_backend_case_record(record = {}, case_type = {}) {
      const identity = case_identity(record, case_type);
      const created = created_date(record);
      const domain = record_domain_label(record, case_type);
      const status = record_status_label(record);
      const workflow_state = fallback_workflow_state(record);

      return base_case_payload({
        record,
        case_type,
        identity,
        created,
        domain,
        status,
        workflow_state,
        facts: [],
        documents: [],
        application_form_id: null,
        answer_sections: [],
      });
    },
};
